class VariantSelector extends HTMLElement{
  constructor(){
    super()
    // Bind methods once in constructor for proper cleanup
    this.handlePopState = this.handlePopState.bind(this)
    this.handleMediaSelected = this.handleMediaSelected.bind(this)
  }
  
  connectedCallback(){
    this.variants = this.querySelectorAll('.variant-selector__item')
    this.priceEl = document.querySelector('.product__price')
    this.compareAtPriceEl = document.querySelector('.product__compare-at-price')
    this.unitPriceEl = document.querySelector('.product__unit-price')
    this.stockContainer = document.querySelector('[data-variant-inventory]')
    this.inStockEl = this.stockContainer?.querySelector('[data-stock-status="in-stock"]')
    this.outOfStockEl = this.stockContainer?.querySelector('[data-stock-status="out-of-stock"]')

    // Remove active-variant from all variants first
    this.variants.forEach((v) => {
      v.removeAttribute('active-variant')
    })

    // Check URL for variant ID
    const urlVariantId = this.getVariantIdFromUrl()
    let activeVariant = null

    if (urlVariantId) {
      // Find variant matching URL ID
      activeVariant = Array.from(this.variants).find(
        (v) => v.dataset.variantId === urlVariantId
      )
    }

    // If no match from URL, use the first variant or existing active variant
    if (!activeVariant) {
      const existingActive = this.querySelector('.variant-selector__item[active-variant]')
      activeVariant = existingActive || (this.variants.length > 0 ? this.variants[0] : null)
    }

    // Set the active variant
    if (activeVariant) {
      activeVariant.setAttribute('active-variant', '')
      this.productPrices(activeVariant)
      this.updateStockStatus(activeVariant)
      // Only update URL if it doesn't match (to avoid unnecessary history entries)
      if (!urlVariantId || urlVariantId !== activeVariant.dataset.variantId) {
        this.updateUrl(activeVariant)
      }
      this.notifyATC(activeVariant)
    }

    this.variants.forEach((variant)=>{
      variant.addEventListener('click', this.handleVariant.bind(this))
    })

    // Listen for browser back/forward navigation and variant-media clicks.
    window.addEventListener('popstate', this.handlePopState)
    document.addEventListener('mediaSelected', this.handleMediaSelected)
  }

  handleMediaSelected(e){
    const variantId = e.detail && e.detail.variantId ? String(e.detail.variantId) : ''
    if (!variantId) return

    const matchingVariant = Array.from(this.variants).find(
      (variant) => variant.dataset.variantId === variantId
    )

    if (!matchingVariant || matchingVariant.hasAttribute('active-variant')) return

    this.variants.forEach((variant) => {
      variant.removeAttribute('active-variant')
    })

    matchingVariant.setAttribute('active-variant', '')
    this.productPrices(matchingVariant)
    this.updateStockStatus(matchingVariant)
    this.updateUrl(matchingVariant)
    this.notifyATC(matchingVariant)
  }

  handlePopState(e){
    const urlVariantId = this.getVariantIdFromUrl()
    if (!urlVariantId) return

    const matchingVariant = Array.from(this.variants).find(
      (v) => v.dataset.variantId === urlVariantId
    )

    if (matchingVariant) {
      // Remove active-variant from all variants
      this.variants.forEach((v) => {
        v.removeAttribute('active-variant')
      })
      
      // Set active-variant on matching variant
      matchingVariant.setAttribute('active-variant', '')
      this.productPrices(matchingVariant)
      this.updateStockStatus(matchingVariant)
      this.notifyATC(matchingVariant)
    }
  }

  getVariantIdFromUrl(){
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('variant')
  }

  handleVariant(e){
    const variant = e.currentTarget
    
    // Remove active-variant from all variants
    this.variants.forEach((v) => {
      v.removeAttribute('active-variant')
    })
    
    // Set active-variant on clicked variant
    variant.setAttribute('active-variant', '')
    
    this.productPrices(variant)
    this.updateStockStatus(variant)
    this.updateUrl(variant)
    this.notifyATC(variant)
  }

  updateUrl(variant){
    const variantId = variant.dataset.variantId
    if (!variantId) return

    const url = new URL(window.location.href)
    url.searchParams.set('variant', variantId)
    window.history.pushState({ variant: variantId }, '', url)
  }

  productPrices(variant){
    // updates the price based on the variant selected
    if(variant.dataset.variantPrice && this.priceEl){
      this.priceEl.textContent = variant.dataset.variantPrice
    }

    // Update optional price elements only when they exist.
    // Some products don't render compare-at or unit price markup. Accessing a
    // missing element here previously stopped initialization before click
    // handlers were registered.
    if(this.compareAtPriceEl){
      this.compareAtPriceEl.textContent = variant.dataset.variantComparePrice || ""
    }

    if(this.unitPriceEl){
      this.unitPriceEl.textContent = variant.dataset.variantUnitPrice || ""
    }
  }

  updateStockStatus(variant){
    if (!this.stockContainer || !this.inStockEl || !this.outOfStockEl) return

    const isAvailable = variant.dataset.variantAvailable === 'true'
    
    if (isAvailable) {
      this.inStockEl.style.display = 'block'
      this.outOfStockEl.style.display = 'none'
    } else {
      this.inStockEl.style.display = 'none'
      this.outOfStockEl.style.display = 'block'
    }
  }

  notifyATC(variant){
    // Dispatch custom event for ATC button to listen
    const event = new CustomEvent('variant-changed', {
      detail: {
        variantId: variant.dataset.variantId,
        available: variant.dataset.variantAvailable === 'true',
        inventoryQuantity: parseInt(variant.dataset.variantInventoryQuantity) || 0
      },
      bubbles: true
    })
    this.dispatchEvent(event)
  }
  disconnectedCallback(){
    // Clean up event listeners
    window.removeEventListener('popstate', this.handlePopState)
    document.removeEventListener('mediaSelected', this.handleMediaSelected)
  }
}

customElements.define('variant-selector', VariantSelector)